import { Link } from "@tanstack/react-router";

import { usePlantProfiles } from "@/lib/api/plant-profiles";
import { usePreferences } from "@/lib/preferences";
import { ecUnitLabel, formatEc, formatTemp, tempUnitLabel } from "@/lib/units";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/error-state";

export function ChemistryPlantProfilesPage() {
  const q = usePlantProfiles();
  const { temperatureUnit, ecUnit } = usePreferences();
  const items = (q.data?.items ?? []).slice().sort((a, b) => {
    if (a.species !== b.species) return a.species.localeCompare(b.species);
    return a.name.localeCompare(b.name);
  });
  const num = (n: number | undefined) => (n === undefined ? "—" : String(n));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Link to="/chemistry-profile/plant-profiles/new">
          <Button size="sm">New plant profile</Button>
        </Link>
      </div>
      {q.error && <ErrorState error={q.error} retry={q.refetch} />}
      {q.isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
      {q.data && items.length === 0 && (
        <p className="text-sm text-muted-foreground">No plant profiles yet.</p>
      )}
      {items.length > 0 && (
        <ul className="divide-y rounded-md border">
          {items.map((p) => (
            <li key={p.id}>
              <Link
                to="/chemistry-profile/plant-profiles/$plantProfileId"
                params={{ plantProfileId: p.id }}
                className="block px-4 py-3 text-sm hover:bg-accent/50"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="font-medium">
                      {p.name}
                      {p.system_provided && (
                        <span className="ml-2 text-[10px] uppercase text-muted-foreground">
                          system
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {[p.species, p.stage || null, p.feeder_class || null]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground tabular-nums">
                    pH {num(p.ph_ideal)} · {ecUnitLabel(ecUnit)}{" "}
                    {formatEc(p.ec_ideal, ecUnit)}
                    <br />
                    Temp {formatTemp(p.temp_ideal_c, temperatureUnit)}
                    {tempUnitLabel(temperatureUnit)}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
